/*
  Проверка должников счёта по реестру банкротств (21.10.2026, решение владельца
  20.10.2026: клиент службы «Арбитр Сведения»).

  Кнопка спрашивает реестр о должниках этого счёта; ход опрашивается, пока
  служба не ответит по всем. Ответ - ПОДСКАЗКА: ничего не записывается, процедуру
  вносит человек формой «Банкротство» выше, сверив найденное. Остановка подачи по
  ошибочной записи - потерянный долг, поэтому автоматики здесь нет.

  Реестр у службы пока тестовый: пока это так, над ответом стоит предупреждение, и
  «не найден» не читается как «не банкрот» (сервер присылает признак `is_test`
  по адресу источника каждого ответа).

  Отказ службы остаётся рядом с кнопкой, обе его части (что случилось и что
  делать) - правило 3 скилла `frontend`.

  Номер проверки уходит вниз, к карточкам (23.10.2026): карточку, найденную только
  по ФИО, можно спросить по номеру («Запросить сообщения», `RegistryCard.jsx`), и
  сервер сверяет, что она из ответа именно этой проверки.
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { Notice } from './Common.jsx'
import RegistryPersonResult from './RegistryPersonResult.jsx'
import { isTemporary, POLL_LIMIT, POLL_MS } from './registryPolling.js'

/* Временный сбой опрос переживает, постоянный останавливает его сразу и отпускает
   кнопку (`registryPolling.isTemporary`; 21.10.2026 - одна ошибка останавливала
   опрос навсегда, 23.10.2026 - постоянный отказ службы, наоборот, опрашивался
   пять минут). */
export default function AccountRegistryCheck({ card, canEdit }) {
  const [lookup, setLookup] = useState(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)
  const [polls, setPolls] = useState(0)
  const accountId = card.account_id

  useEffect(() => {
    if (!lookup || lookup.done || polls >= POLL_LIMIT) return undefined
    // Ответ опроса, ушедшего до нового нажатия «Проверить ещё раз», выбрасывается
    // (аудит 26.10.2026, причина 5): он затирал бы свежую проверку прежней.
    let current = true
    const timer = setTimeout(async () => {
      try {
        const answer = await api.registryCheck(accountId, lookup.check_id)
        if (!current) return
        setLookup(answer)
        setFailure(null)
        setPolls(polls + 1)
      } catch (error) {
        if (!current) return
        setFailure(error)
        setPolls(isTemporary(error) ? polls + 1 : POLL_LIMIT)
      }
    }, POLL_MS)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [accountId, lookup, polls])

  if (!canEdit) return null

  async function start() {
    setBusy(true)
    setFailure(null)
    setPolls(0)
    try {
      setLookup(await api.startRegistryCheck(accountId))
    } catch (error) {
      setFailure(error)
    } finally {
      setBusy(false)
    }
  }

  const running = lookup && !lookup.done && polls < POLL_LIMIT

  return (
    <section className="section card">
      <h2 className="section__title">Проверка по реестру банкротств</h2>
      <p className="muted">
        Арбитр ЖКХ спросит реестр о должниках этого счёта. Ответ - подсказка: ничего не
        записывается, а процедуру вносите вы формой «Банкротство» выше, сверив, тот ли это
        человек.
      </p>
      <button type="button" className="button" disabled={busy || running} onClick={start}>
        {lookup ? 'Проверить ещё раз' : 'Проверить по реестру'}
      </button>
      <Notice error={failure} />

      {lookup?.is_test ? (
        <Notice kind="warn">
          Реестр пока тестовый ({lookup.source_title}): ответы - демонстрационные данные.
          «Не найден» здесь не значит «не банкрот», и решение о подаче по этим ответам
          принимать нельзя.
        </Notice>
      ) : null}
      {running ? (
        <p className="muted">
          Проверено {lookup.answered} из {lookup.total}…
        </p>
      ) : null}
      {lookup && !lookup.done && polls >= POLL_LIMIT && !failure ? (
        <p className="muted">
          Проверка идёт дольше обычного. Нажмите «Проверить ещё раз» позже: уже полученные
          ответы служба сохранила.
        </p>
      ) : null}
      {lookup
        ? lookup.people.map((result) => (
            <RegistryPersonResult
              key={result.person_id}
              result={result}
              isTest={lookup.is_test}
              ask={{ accountId, checkId: lookup.check_id }}
            />
          ))
        : null}
    </section>
  )
}
